import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/auth';
import { authAPI, adminAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Mail, Building2, Shield, Bell, Key, Calendar, Activity, Camera, UserCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { AvatarUpload } from '../ui/avatar-upload';

export function ProfilePage() {
  const { user, logout, setUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [roles, setRoles] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [notifications, setNotifications] = useState({
    emailAlerts: true,
    dealUpdates: true,
    activityReminders: true,
    weeklyDigest: false,
  });
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [passwordSaving, setPasswordSaving] = useState(false);

  useEffect(() => {
    loadProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async () => {
    try {
      const res = await authAPI.me();
      setProfile(res.data);
      setUser?.(res.data);
      localStorage.setItem('user', JSON.stringify(res.data));
      try {
        const rolesRes = await adminAPI.listRoles();
        setRoles(rolesRes.data || []);
      } catch {}
    } catch {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (file) => {
    try {
      const res = await authAPI.uploadMyAvatar(file);
      const nextProfile = { ...profile, avatar_url: res.data.avatar_url };
      setProfile(nextProfile);
      setUser?.(nextProfile);
      localStorage.setItem('user', JSON.stringify(nextProfile));
      toast.success('Profile photo updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    }
  };

  const handleNotificationChange = (key) => {
    setNotifications((prev) => ({ ...prev, [key]: !prev[key] }));
    toast.success('Notification preferences updated');
  };

  const handlePasswordUpdate = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error('Enter current and new password');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New password confirmation does not match');
      return;
    }
    setPasswordSaving(true);
    try {
      await authAPI.changePassword({ current_password: passwordForm.current_password, new_password: passwordForm.new_password });
      toast.success('Password updated successfully');
      setPasswordDialogOpen(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setPasswordSaving(false);
    }
  };

  const getUserRoleNames = () => {
    if (!profile?.roles || !roles.length) return profile?.roles || [];
    return profile.roles.map((roleId) => roles.find((role) => role.id === roleId)?.name || roleId);
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="profile-page-loading">
        <Skeleton className="h-10 w-56" />
        <div className="grid grid-cols-1 lg:grid-cols-[340px_minmax(0,1fr)] gap-6">
          <Skeleton className="h-[520px] rounded-3xl" />
          <Skeleton className="h-[520px] rounded-3xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <h1 className="page-header-title">My Profile</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your identity, visibility, and account security.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[360px_minmax(0,1fr)] gap-6">
        <Card className="rounded-3xl border-slate-200 shadow-sm overflow-hidden">
          <CardContent className="p-8">
            <div className="flex flex-col items-center text-center">
              <AvatarUpload
                src={profile?.avatar_url}
                alt={profile?.name}
                name={profile?.name || profile?.email}
                size="2xl"
                onUpload={handleAvatarUpload}
                dataTestId="profile-avatar-upload"
              />
              <div className="mt-8 space-y-2">
                <h2 className="text-4xl font-bold tracking-tight text-slate-900">{profile?.name || 'User'}</h2>
                <p className="text-lg text-slate-500">{profile?.email}</p>
              </div>
              <div className="flex flex-wrap gap-2 justify-center mt-5">
                {getUserRoleNames().map((role) => (
                  <Badge key={role} variant="outline" className="rounded-full bg-slate-100 text-slate-700 border-slate-200 px-3 py-1 text-xs font-medium">
                    {role}
                  </Badge>
                ))}
              </div>
              <Badge className="mt-5 rounded-full bg-[#800000] text-white px-4 py-1.5 text-sm capitalize shadow-sm" data-testid="profile-status-badge">
                {profile?.status || 'approved'}
              </Badge>
            </div>

            <div className="mt-8 pt-8 border-t border-slate-100 grid gap-4">
              {[
                { icon: Mail, label: 'Email', value: profile?.email },
                { icon: Building2, label: 'Organization', value: profile?.org_id || 'Default' },
                { icon: Calendar, label: 'Member Since', value: profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A' },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                    <div className="w-10 h-10 rounded-2xl bg-white border border-slate-100 flex items-center justify-center">
                      <Icon className="h-4 w-4 text-[#800000]" />
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.15em] text-slate-400 font-semibold">{item.label}</p>
                      <p className="text-sm font-medium text-slate-800">{item.value}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-3xl border-slate-200 shadow-sm">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-semibold tracking-tight text-slate-900">Account Controls</CardTitle>
              <CardDescription className="text-slate-500">Keep your profile polished and your account secure.</CardDescription>
              <TabsList className="grid w-full grid-cols-3 bg-slate-100 rounded-2xl p-1 h-auto mt-4">
                <TabsTrigger value="overview" data-testid="tab-profile-overview" className="rounded-xl py-3"><UserCircle2 className="h-4 w-4 mr-2" />Overview</TabsTrigger>
                <TabsTrigger value="notifications" data-testid="tab-notifications" className="rounded-xl py-3"><Bell className="h-4 w-4 mr-2" />Notifications</TabsTrigger>
                <TabsTrigger value="security" data-testid="tab-security" className="rounded-xl py-3"><Shield className="h-4 w-4 mr-2" />Security</TabsTrigger>
              </TabsList>
            </CardHeader>

            <CardContent className="pt-0 pb-8 px-8">
              <TabsContent value="overview" className="mt-0 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="rounded-2xl border-slate-100 bg-slate-50">
                    <CardContent className="p-5 space-y-2">
                      <div className="flex items-center gap-2 text-slate-800"><Camera className="h-4 w-4 text-[#800000]" /><p className="font-semibold">Profile Photo</p></div>
                      <p className="text-sm text-slate-500">Upload a circular photo used in your profile, sidebar, and admin user directory.</p>
                      <Button variant="outline" className="rounded-full" onClick={() => document.querySelector('[data-testid="profile-avatar-upload-input"]')?.click()} data-testid="profile-avatar-cta">Change Photo</Button>
                    </CardContent>
                  </Card>
                  <Card className="rounded-2xl border-slate-100 bg-slate-50">
                    <CardContent className="p-5 space-y-2">
                      <div className="flex items-center gap-2 text-slate-800"><Shield className="h-4 w-4 text-[#800000]" /><p className="font-semibold">Access Roles</p></div>
                      <p className="text-sm text-slate-500">Your visibility and dashboard content are controlled by your assigned roles.</p>
                      <div className="flex flex-wrap gap-2 pt-1">
                        {getUserRoleNames().map((role) => <Badge key={role} className="rounded-full bg-white border border-slate-200 text-slate-700">{role}</Badge>)}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="notifications" className="mt-0 space-y-4">
                {[
                  ['emailAlerts', 'Email Alerts', 'Receive important system and business updates.'],
                  ['dealUpdates', 'Deal Updates', 'Get notified when opportunity stages or ownership change.'],
                  ['activityReminders', 'Activity Reminders', 'Stay on top of upcoming activities and follow-ups.'],
                  ['weeklyDigest', 'Weekly Digest', 'Get a weekly summary of performance and pipeline.'],
                ].map(([key, title, subtitle]) => (
                  <div key={key} className="flex items-center justify-between p-5 rounded-2xl border border-slate-100 bg-slate-50">
                    <div className="space-y-1">
                      <p className="font-semibold text-slate-800">{title}</p>
                      <p className="text-sm text-slate-500">{subtitle}</p>
                    </div>
                    <Switch checked={notifications[key]} onCheckedChange={() => handleNotificationChange(key)} data-testid={`toggle-${key}`} />
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="security" className="mt-0 space-y-5">
                <Card className="rounded-2xl border-slate-100 bg-slate-50">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2"><Key className="h-4 w-4 text-[#800000]" />Password & Session Hygiene</CardTitle>
                    <CardDescription>Change password and keep your active sessions under control.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="rounded-2xl border bg-white px-4 py-3 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm text-slate-800">Current Session</p>
                        <p className="text-xs text-slate-500">This browser session is active now.</p>
                      </div>
                      <Badge className="rounded-full bg-emerald-100 text-emerald-700">Active</Badge>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <Button variant="outline" data-testid="change-password-btn" onClick={() => setPasswordDialogOpen(true)}>Update Password</Button>
                      <Button variant="destructive" onClick={logout} data-testid="logout-btn">Sign Out</Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>
      </div>

      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-md" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Update Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-500">Current Password</Label>
              <Input type="password" value={passwordForm.current_password} onChange={e => setPasswordForm(f => ({ ...f, current_password: e.target.value }))} data-testid="current-password-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">New Password</Label>
              <Input type="password" value={passwordForm.new_password} onChange={e => setPasswordForm(f => ({ ...f, new_password: e.target.value }))} data-testid="new-password-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Confirm New Password</Label>
              <Input type="password" value={passwordForm.confirm_password} onChange={e => setPasswordForm(f => ({ ...f, confirm_password: e.target.value }))} data-testid="confirm-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
            <Button onClick={handlePasswordUpdate} disabled={passwordSaving} className="bg-[#800000] hover:bg-[#9a1919] text-white" data-testid="save-password-btn">{passwordSaving ? 'Saving...' : 'Save Password'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
